import { MouseEvent, useMemo, useState } from 'react';
import cn from 'classnames';
import { Form } from 'react-bootstrap';
import { MdArrowDownward, MdArrowUpward } from 'react-icons/md';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { TableEmptyState } from '@Components/TableEmptyState';
import { searchItems } from '@src/utils/search';
import { ColumnFilterPopover } from './ColumnFilterPopover';
import { ProductCard } from './ProductCard';
import {
  AUDIENCE_ICON,
  IconChip,
  KIND_ICON,
  ParentBreadcrumb,
  SCOPE_ICON,
  TagList
} from './ProductChips';
import {
  FilterFacet,
  FilterFacetType,
  getStatusBucket,
  Product,
  SortDirection,
  SortDirectionType,
  SortField,
  SortFieldType
} from './product-roadmap-constants';
import {
  DESCRIPTION_SEARCH_KEYS,
  distinctFacetValues,
  FacetSelection,
  fullDescription,
  shouldRowClickOpenCard,
  sortProducts
} from './product-roadmap-utils';

interface ColumnLabelProps {
  field: SortFieldType;
  label: string;
  sortField: SortFieldType;
  sortDirection: SortDirectionType;
  onSort: (field: SortFieldType) => void;
}

const ColumnLabel = ({ field, label, sortField, sortDirection, onSort }: ColumnLabelProps) => (
  <InteractiveSpan className='product-roadmap-col-label' onClick={() => onSort(field)}>
    {label}{' '}
    {sortField === field &&
      (sortDirection === SortDirection.ASC ? <MdArrowUpward /> : <MdArrowDownward />)}
  </InteractiveSpan>
);

interface FacetColumnHeaderProps extends ColumnLabelProps {
  facet: FilterFacetType;
  scopedProducts: Product[];
  selected: ReadonlySet<string>;
  colorMap?: Record<string, string>;
  onFacetChange: (facet: FilterFacetType, next: ReadonlySet<string>) => void;
}

// Column header with an inline filter popover -- the header row IS the filter menu, matching the
// retired HTML prototype's per-column filter popovers, rather than a separate always-visible
// chip bank above the table.
const FacetColumnHeader = ({
  facet,
  scopedProducts,
  selected,
  colorMap,
  onFacetChange,
  ...labelProps
}: FacetColumnHeaderProps) => {
  const options = useMemo(
    () => distinctFacetValues(scopedProducts, facet),
    [scopedProducts, facet]
  );
  return (
    <div className='product-roadmap-col-header'>
      <ColumnLabel {...labelProps} />
      <ColumnFilterPopover
        label={labelProps.label}
        options={options}
        selected={selected}
        colorMap={colorMap}
        onChange={next => onFacetChange(facet, next)}
      />
    </div>
  );
};

interface TextColumnHeaderProps extends ColumnLabelProps {
  query: string;
  placeholder: string;
  onQueryChange: (query: string) => void;
}

const TextColumnHeader = ({
  query,
  placeholder,
  onQueryChange,
  ...labelProps
}: TextColumnHeaderProps) => (
  <div className='product-roadmap-col-header'>
    <ColumnLabel {...labelProps} />
    <Form.Control
      className='product-roadmap-col-filter'
      type='text'
      size='sm'
      placeholder={placeholder}
      value={query}
      onChange={e => onQueryChange(e.target.value)}
    />
  </div>
);

interface NameColumnHeaderProps extends ColumnLabelProps {
  query: string;
  onQueryChange: (query: string) => void;
  parentOptions: { value: string; count: number }[];
  parentSelected: ReadonlySet<string>;
  onParentChange: (next: ReadonlySet<string>) => void;
}

// Parent has no dedicated table column of its own (unlike Function) -- its value is shown inline
// as a breadcrumb next to the product name instead, per Sean, so this is the one place a filter
// popover (reusing the same ColumnFilterPopover as every other facet) hangs off the Name header
// rather than a column of its own. Sits in the same row as the search box, pinned to the far
// right (the search input's flex:1 pushes it there), not stacked below -- a stacked layout read
// as visually unbalanced against the rest of the header row.
const NameColumnHeader = ({
  query,
  onQueryChange,
  parentOptions,
  parentSelected,
  onParentChange,
  ...labelProps
}: NameColumnHeaderProps) => (
  <div className='product-roadmap-col-header'>
    <ColumnLabel {...labelProps} />
    <div className='product-roadmap-name-header-row'>
      <Form.Control
        className='product-roadmap-col-filter'
        type='text'
        size='sm'
        placeholder='Filter product, kind, audience, scope...'
        value={query}
        onChange={e => onQueryChange(e.target.value)}
      />
      <ColumnFilterPopover
        label='Parent'
        options={parentOptions}
        selected={parentSelected}
        onChange={onParentChange}
      />
    </div>
  </div>
);

interface ProductRowProps {
  product: Product;
  functionColorMap: Record<string, string>;
  onOpenCard: (product: Product) => void;
}

const ProductRow = ({ product, functionColorMap, onOpenCard }: ProductRowProps) => {
  const { style } = getStatusBucket(product.life);
  const description = fullDescription(product);

  // The whole row opens the card, so the full description is a click away from its snippet.
  // Clicks on the product name are left to the name (it opens the card itself); see
  // shouldRowClickOpenCard for why the row has no role/tabIndex of its own.
  const handleRowClick = (e: MouseEvent<HTMLDivElement>) => {
    if (shouldRowClickOpenCard(e.target, window.getSelection()?.toString() ?? '')) {
      onOpenCard(product);
    }
  };

  return (
    <div
      className={cn('product-roadmap-row', {
        [`accent-design-${style.design}`]: style.design,
        [`accent-intent-${style.intent}`]: style.intent,
        [`accent-custom-${style.custom}`]: style.custom
      })}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- a mouse/touch convenience
          that catches bubbled clicks; the product name is the row's accessible control, and a row
          relabelled as a button would orphan its cells (see InteractiveEl). */}
      <div className='product-roadmap-row-main' onClick={handleRowClick}>
        <div className='product-roadmap-cell-name'>
          <div className='product-roadmap-cell-name-title-row'>
            <InteractiveSpan
              className='product-roadmap-cell-name-title'
              onClick={() => onOpenCard(product)}
            >
              {product.name}
            </InteractiveSpan>
            <span className='product-roadmap-cell-name-icons'>
              <IconChip facet='kind' value={product.kind} iconMap={KIND_ICON} />
              <IconChip facet='audience' value={product.audience} iconMap={AUDIENCE_ICON} />
              <IconChip facet='scope' value={product.scope} iconMap={SCOPE_ICON} />
            </span>
          </div>
          <ParentBreadcrumb parent={product.parent} />
        </div>
        <span className='product-roadmap-cell-description' title={description || undefined}>
          {description || <em>No description on file</em>}
        </span>
        <TagList values={product.function} colorMap={functionColorMap} />
        <span className='product-roadmap-cell-target' title={product.target || undefined}>
          {product.target}
        </span>
        <span className='product-roadmap-cell-owner'>{product.owner || '—'}</span>
      </div>
    </div>
  );
};

interface ProductRoadmapTableProps {
  products: Product[];
  scopedProducts: Record<FilterFacetType, Product[]>;
  selection: FacetSelection;
  functionColorMap: Record<string, string>;
  onFacetChange: (facet: FilterFacetType, next: ReadonlySet<string>) => void;
}

export const ProductRoadmapTable = ({
  products,
  scopedProducts,
  selection,
  functionColorMap,
  onFacetChange
}: ProductRoadmapTableProps) => {
  const [sortField, setSortField] = useState<SortFieldType>(SortField.NAME);
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.ASC);
  const [nameQuery, setNameQuery] = useState('');
  const [descriptionQuery, setDescriptionQuery] = useState('');
  const [ownerQuery, setOwnerQuery] = useState('');
  const [cardProduct, setCardProduct] = useState<Product | null>(null);

  const handleSort = (field: SortFieldType) => {
    if (sortField === field) {
      setSortDirection(
        sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC
      );
    } else {
      setSortField(field);
      setSortDirection(SortDirection.ASC);
    }
  };

  const visibleProducts = useMemo(() => {
    const byName = searchItems(products, nameQuery, {
      keys: ['name', 'kind', 'audience', 'scope'],
      rank: false
    });
    const byDescription = searchItems(byName, descriptionQuery, {
      keys: DESCRIPTION_SEARCH_KEYS,
      rank: false
    });
    const filtered = searchItems(byDescription, ownerQuery, { keys: ['owner'], rank: false });
    return sortProducts(filtered, sortField, sortDirection);
  }, [products, nameQuery, descriptionQuery, ownerQuery, sortField, sortDirection]);

  const parentOptions = useMemo(
    () => distinctFacetValues(scopedProducts[FilterFacet.PARENT], FilterFacet.PARENT),
    [scopedProducts]
  );

  const headerProps = { sortField, sortDirection, onSort: handleSort };

  return (
    <div className='product-roadmap-table'>
      <div className='product-roadmap-table-header'>
        <NameColumnHeader
          field={SortField.NAME}
          label='Product'
          query={nameQuery}
          onQueryChange={setNameQuery}
          parentOptions={parentOptions}
          parentSelected={selection[FilterFacet.PARENT]}
          onParentChange={next => onFacetChange(FilterFacet.PARENT, next)}
          {...headerProps}
        />
        <TextColumnHeader
          field={SortField.DESCRIPTION}
          label='Description'
          query={descriptionQuery}
          placeholder='Filter description...'
          onQueryChange={setDescriptionQuery}
          {...headerProps}
        />
        <FacetColumnHeader
          field={SortField.FUNCTION}
          label='Function'
          facet={FilterFacet.FUNCTION}
          scopedProducts={scopedProducts[FilterFacet.FUNCTION]}
          selected={selection[FilterFacet.FUNCTION]}
          colorMap={functionColorMap}
          onFacetChange={onFacetChange}
          {...headerProps}
        />
        <ColumnLabel field={SortField.TARGET} label='Target' {...headerProps} />
        <TextColumnHeader
          field={SortField.OWNER}
          label='Owner'
          query={ownerQuery}
          placeholder='Filter owner...'
          onQueryChange={setOwnerQuery}
          {...headerProps}
        />
      </div>

      {visibleProducts.length === 0 ? (
        <TableEmptyState message='No products match the current filters.' />
      ) : (
        visibleProducts.map(p => (
          <ProductRow
            key={p.name}
            product={p}
            functionColorMap={functionColorMap}
            onOpenCard={setCardProduct}
          />
        ))
      )}

      <ProductCard
        product={cardProduct}
        functionColorMap={functionColorMap}
        onHide={() => setCardProduct(null)}
      />
    </div>
  );
};
